import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const StudyInJapan: React.FC = () => {
  const [hoveredService, setHoveredService] = useState<number | null>(null);

  const services = [
    {
      title: "School Selection",
      description: "Find the perfect language school or university that matches your goals",
      icon: "🏫",
      link: "/study/schools",
      color: "from-blue-500 to-indigo-600",
      features: ["Top-ranked institutions", "Program matching", "Location guidance"],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(111, 123, 247) 0%, rgb(72, 149, 239) 100.2%)"
    },
    {
      title: "Application Support",
      description: "Complete assistance with admission and visa application process",
      icon: "📝",
      link: "/study/application",
      color: "from-purple-500 to-pink-600",
      features: ["Document preparation", "Visa guidance", "Interview prep"],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(186, 116, 247) 0%, rgb(239, 72, 172) 100.2%)"
    },
    {
      title: "Accommodation",
      description: "Help finding suitable student housing or dormitory options",
      icon: "🏠",
      link: "/study/accommodation",
      color: "from-orange-500 to-red-600",
      features: ["Dormitory options", "Private housing", "Location assistance"],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(255, 126, 95) 0%, rgb(254, 180, 123) 100.2%)"
    }
  ];

  return (
    <div className="min-h-screen pt-24 relative overflow-hidden bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-50 via-gray-100 to-gray-200">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 30 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full mix-blend-multiply filter blur-xl opacity-20"
            style={{
              width: 100 + Math.random() * 300,
              height: 100 + Math.random() * 300,
              background: i % 3 === 0 
                ? 'rgba(59, 130, 246, 0.2)' 
                : i % 3 === 1 
                  ? 'rgba(139, 92, 246, 0.2)' 
                  : 'rgba(236, 72, 153, 0.2)',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
            animate={{
              x: [0, Math.random() * 100 - 50],
              y: [0, Math.random() * 100 - 50],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: 15 + Math.random() * 15,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: Math.random() * 5
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center mb-24"
        >
          <motion.div 
            className="inline-block mb-6"
            animate={{ 
              y: [0, -10, 0]
            }}
            transition={{ 
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <h2 className="text-6xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary drop-shadow-lg">
              Study in Japan
            </h2>
          </motion.div>
          <motion.div
            className="h-1 w-24 bg-gradient-to-r from-primary to-secondary mx-auto mb-6 rounded-full"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          />
          <motion.p 
            className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto font-medium"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            Your gateway to quality education in Japan. We provide comprehensive support for your academic journey.
          </motion.p>
        </motion.div>

        {/* Services Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-24">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              onHoverStart={() => setHoveredService(index)}
              onHoverEnd={() => setHoveredService(null)}
              className="relative group"
            >
              <motion.div 
                className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ 
                  background: service.bgPattern,
                  opacity: 0.4
                }}
              />
              <motion.div 
                className="relative p-8 h-full flex flex-col bg-white/80 backdrop-blur-sm rounded-3xl"
                whileHover={{ y: -10 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div 
                  className="text-5xl mb-8"
                  animate={hoveredService === index ? {
                    y: [0, -5, 0]
                  } : {}}
                  transition={{ duration: 0.5 }}
                >
                  {service.icon}
                </motion.div>
                <motion.h3 
                  className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-gray-900"
                >
                  {service.title}
                </motion.h3>
                <motion.p 
                  className="text-gray-700 mb-6 group-hover:text-gray-900"
                >
                  {service.description}
                </motion.p>
                <div className="space-y-3 mb-8 flex-grow">
                  {service.features.map((feature, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ 
                        opacity: 1, 
                        x: 0,
                        transition: { duration: 0.3, delay: i * 0.1 }
                      }}
                      className="flex items-center text-gray-700 group-hover:text-gray-900"
                    >
                      <motion.div 
                        className="w-3 h-3 rounded-full mr-3 flex items-center justify-center"
                        style={{ 
                          background: service.bgPattern
                        }}
                        animate={{ 
                          scale: [1, 1.2, 1],
                          transition: { duration: 0.5, delay: i * 0.1 }
                        }}
                      >
                        <motion.div 
                          className="w-1.5 h-1.5 bg-white rounded-full"
                          animate={{ 
                            scale: [0, 1],
                            transition: { duration: 0.3, delay: i * 0.1 }
                          }}
                        />
                      </motion.div>
                      {feature}
                    </motion.div>
                  ))}
                </div>
                <Link to={service.link} className="mt-auto">
                  <motion.div
                    className="inline-flex items-center text-gray-900 font-semibold group-hover:text-blue-900"
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Explore More
                    <motion.svg 
                      className="w-6 h-6 ml-2"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      animate={hoveredService === index ? { 
                        x: [0, 5, 0]
                      } : {}}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </motion.svg>
                  </motion.div>
                </Link>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center relative"
        >
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-3xl -z-10 blur-xl"
            animate={{
              opacity: [0.5, 0.8, 0.5]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.p 
            className="text-2xl text-gray-700 mb-10 font-medium"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Ready to start your journey? Let us help you make your dream of studying in Japan a reality.
          </motion.p>
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-5 bg-gradient-to-r from-primary to-secondary text-white text-lg font-bold rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 relative overflow-hidden group mb-4"
            >
              <motion.span 
                className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                animate={{
                  x: ['-100%', '100%']
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              Get Started Today
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default StudyInJapan; 